import React, { useEffect, useState, useRef } from 'react';
import { ZapIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
export function WhatIsSCB300() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.2
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const items = en ? [{
    icon: <ZapIcon className="w-6 h-6 text-brand-yellow" data-id="element-240" />,
    label: 'Electric drive',
    text: 'A powerful motor for easy climbing with minimal operator effort.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6 text-brand-yellow" data-id="element-241" />,
    label: 'Control & safety',
    text: 'Integrated automatic brake guarantees load stability at all times.'
  }, {
    icon: <UserIcon className="w-6 h-6 text-brand-yellow" data-id="element-242" />,
    label: 'Single operator',
    text: 'Designed so one person can safely handle heavy cargo without extra help.'
  }] : [{
    icon: <ZapIcon className="w-6 h-6 text-brand-yellow" data-id="element-243" />,
    label: 'Električni pogon',
    text: 'Snažan motor za lako savladavanje uspona uz minimalan napor operatera.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6 text-brand-yellow" data-id="element-244" />,
    label: 'Kontrola i bezbednost',
    text: 'Integrisana automatska kočnica garantuje stabilnost tereta u svakom trenutku.'
  }, {
    icon: <UserIcon className="w-6 h-6 text-brand-yellow" data-id="element-245" />,
    label: 'Jedan operater',
    text: 'Dizajnirano da jedna osoba bezbedno manipuliše teškim teretom bez dodatne pomoći.'
  }];
  const footer = en ? 'D-TECH offers a range of electric stair-climbing machines and cargo transport solutions, designed for professional use in construction, logistics, warehouses and delivery services. Combining power, stability and simple operation in compact systems.' : 'D-TECH nudi električne mašine za transport tereta uz stepenice i industrijska kolica, namenjene profesionalnoj upotrebi u građevini, logistici, skladištima i uslugama dostave. Snaga, stabilnost i jednostavno rukovanje u kompaktnim sistemima.';
  return <section className="py-14 md:py-20 bg-dark-surface border-b border-dark-border" data-id="element-246">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-248">
          <div data-id="element-249">
            <h2 className={`text-3xl md:text-4xl font-black text-white mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} data-id="element-250">
              {en ? 'How does ' : 'Kako funkcioniše '}
              <span className="text-brand-yellow" data-id="element-251">
                {en ? 'D-TECH?' : 'D-TECH?'}
              </span>
            </h2>

            <div className="flex flex-col gap-6" data-id="element-252">
              {items.map((item, i) => <div key={i} className="flex flex-col gap-2 p-5 bg-dark-bg border border-dark-border rounded-industrial" style={{
              transitionProperty: 'opacity, transform',
              transitionDuration: '700ms',
              transitionDelay: `${i * 150}ms`,
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(24px)'
            }} data-id="element-253">
                  <div className="flex items-center gap-3" data-id="element-254">
                    {item.icon}
                    <span className="text-brand-yellow font-bold text-lg" data-id="element-255">
                      {item.label}
                    </span>
                  </div>
                  <p className="text-gray-300 text-base leading-relaxed" data-id="element-256">
                    {item.text}
                  </p>
                </div>)}
            </div>
          </div>

          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} data-id="element-257">
            <div className="bg-dark-bg border border-dark-border rounded-industrial p-8" data-id="element-258">
              <div className="flex justify-center mb-8" data-id="element-259">
                <img src="https://cdn.magicpatterns.com/uploads/m5nEPzJD6tLEnedUiegTe9/basic_1.jpg" alt="D-TECH Machine" className="h-64 object-contain" data-id="element-260" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-6 border-b border-dark-border pb-4" data-id="element-261">
                {en ? 'Key Specifications' : 'Ključne specifikacije'}
              </h3>
              <ul className="space-y-4 text-gray-300" data-id="element-262">
                <li className="flex items-start gap-3" data-id="element-263">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow mt-2 flex-shrink-0" data-id="element-264" />
                  <div data-id="element-265">
                    <span className="font-bold text-white" data-id="element-266">
                      {en ? 'Capacity:' : 'Nosivost:'}
                    </span>{' '}
                    do 200 kg
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-267">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow mt-2 flex-shrink-0" data-id="element-268" />
                  <div data-id="element-269">
                    <span className="font-bold text-white" data-id="element-270">
                      {en ? 'Battery:' : 'Baterija:'}
                    </span>{' '}
                    48V 36Ah (Li-ion)
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-271">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow mt-2 flex-shrink-0" data-id="element-272" />
                  <div data-id="element-273">
                    <span className="font-bold text-white" data-id="element-274">
                      {en ? 'Speed:' : 'Brzina:'}
                    </span>{' '}
                    Podesiva, do 35 stepenika/min
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-275">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow mt-2 flex-shrink-0" data-id="element-276" />
                  <div data-id="element-277">
                    <span className="font-bold text-white" data-id="element-278">
                      {en ? 'Material:' : 'Materijal:'}
                    </span>{' '}
                    Čelik visoke čvrstoće
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}